import { Component, DestroyRef, effect, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Playlist } from '@shared/interfacres/playlist';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

import { debounceTime, firstValueFrom } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';

import { MatDialog } from '@angular/material/dialog';

import { MatProgressBar } from '@angular/material/progress-bar';
import { MatCheckbox } from '@angular/material/checkbox';

import {SelectionModel} from '@angular/cdk/collections';
import { ConfirmationDialog } from '@shared/components/confirmation-dialog/confirmation-dialog';

import { PlaylistsManagerStore } from '@data-access/manager/stores/playlists-manager.store';
import { EditPlaylistTracksDialog } from './edit-playlist-tracks-dialog/edit-playlist-tracks-dialog';
import { IconInput } from '@shared/components/icon-input/icon-input';
import { EditPlaylistDataDialog } from './edit-playlist-data-dialog/edit-playlist-data-dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';


@Component({
  selector: 'app-playlists',
  templateUrl: './playlists.html',
  styleUrl: './playlists.scss',

  imports: [MatFormFieldModule, MatInputModule, MatTableModule, MatSortModule, MatPaginatorModule,
    DatePipe, MatIcon, ReactiveFormsModule, MatProgressBar, MatCheckbox, MatButton, IconInput, MatSlideToggle],
})
export class ManagerPlaylists implements OnInit, OnDestroy {
  displayedColumns: string[] = ['id', 'name', 'tracks', 'public', 'updatedAt', 'actions'];
  dataSource: Playlist[] = [];
  total = 0;

  searchValue = new FormControl('');

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  selection = new SelectionModel<number>(
    true,
    []
  );
  readonly store = inject(PlaylistsManagerStore);
  private destroyRef = inject(DestroyRef);
  readonly dialog = inject(MatDialog);

  constructor(
  ) {

    effect(() => {
      this.dataSource = this.store.items();
      this.total = this.store.total();
    });
  }

  ngOnInit(): void {
    this.searchValue.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400),
      )
      .subscribe(input => {
        this.store.getByName(input as string);
      })
    this.store.setFilter('updated_at', 'desc');
  }

  changePage(page: {pageIndex: number; pageSize: number}) {
    this.store.setPagination(page.pageIndex + 1, page.pageSize);
  }

  sortChange(event: {active: string; direction: string}) {
    this.store.setFilter(event.active, event.direction as 'asc' | 'desc');

  }

  async add() {
    const dialogRef = this.dialog.open(EditPlaylistDataDialog, {
      data: null,
      disableClose: true,
      maxWidth: '600px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result) {
      this.store.create(result);
    }
  }

  async edit(item: Playlist) {
    const dialogRef = this.dialog.open(EditPlaylistDataDialog, {
      data: {
        ...item
      },
      disableClose: true,
      maxWidth: '600px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result) {
     this.store.update(item.id, result);
    }
  }

  async changeStatus(isChecked: boolean, id: number) {
    this.store.updatePartial(id, {isPublic: isChecked});
  }

  async getPlaylist(item: Playlist) {

    const dialogRef = this.dialog.open(EditPlaylistTracksDialog, {
      data: {
        name: item.name,
        tracks: item.tracks,
      },
      disableClose: true,
      maxWidth: '1400px',
      width: '70%',
      height:'60%',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      this.store.deleteTracksFromPlaylist(item.id, result);
    }
  }

  async bulkDelete() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'Delete selected',
        message: 'Are you sure you want to delete selected genres?'
      },
      disableClose: true,
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result) {
      this.store.delete(this.selection.selected);
      this.selection.clear();
    }
  }

  ngOnDestroy() {
    this.store.clearCriteria();
  }

}
